import {createDivIn, createImgIn, createParagraphIn, setElementInnerHTML, setElementOnClick} from '../ui_utils/DomUtils'
import {ColonistMembershipProduct} from '../../../../shared/store/ColonistMembershipProduct'
import {CA} from '../analytics/Analytics'
import {doActionIfCanGiftMembership, doActionIfLoggedIn} from './StoreUtils'
import {UIAPIEndpoints} from '../network/UIAPIEndpoints'
import {UICheckoutAjaxEndPoints} from '../ui_checkout/UICheckoutAjaxEndPoints'
import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {AnalyticEventValue, AnalyticEventCategory, AnalyticEventType, GiftMembershipMethod} from '../../../../shared/entity_datas/EntityDatas'
import {UIMembershipProductView} from './UIMembershipProductView'
import {UIStoreState} from './UIStoreState'
import {TranslationObject} from '@colonist/i18n'
import {PaymentMethod} from '../../../../shared/store/BaseColonistStoreProduct'
import {UIPopupController} from '../ui_popup/UIPopupController'
import {ColonistMembershipType, LobbyImage} from '@colonist/types'
import {ClientUserState} from '../ClientUserState'
import {UIStoreAjaxEndPoints} from './UIStoreAjaxEndPoints'
import {ViewPopup} from '../ui_popup/ViewPopup'
import {UIStoreGiftOverlayController} from './gift/UIStoreGiftOverlayController'
import {navigateToPage} from '../ui_utils/ClientUtils.ts'
import {AudioManager, AudioType} from '@colonist/audio'

export class UIStoreMembershipProductView extends UIMembershipProductView {
    protected readonly disclaimerText: HTMLParagraphElement
    protected membershipPrice: HTMLHeadingElement
    protected readonly priceDiv: HTMLDivElement
    protected readonly giftButton: HTMLDivElement

    readonly buttonsContainer: HTMLDivElement
    readonly subscribeButton: HTMLDivElement
    readonly subscribeButtonText: HTMLParagraphElement

    private readonly storeState: UIStoreState
    private readonly localizedPrice: string | undefined

    constructor(container: HTMLDivElement, storeState: UIStoreState, membershipProduct: ColonistMembershipProduct, giftOverlay?: UIStoreGiftOverlayController, localizedPrice?: string) {
        super(container, membershipProduct)
        this.storeState = storeState
        this.localizedPrice = localizedPrice

        this.createDefaultAction()
        this.priceDiv = createDivIn(this.productDiv, 'store-page-membership-price', '')
        const price = this.getMembershipPriceString()
        this.membershipPrice = createParagraphIn(this.priceDiv, {key: 'strings:storePage.sections.membership.billing.paidMonthly', options: {context: 'biggerPrice', amount: price}}, '', '')

        const shouldShowStrikethroughPrice = membershipProduct.billedAnnually
        if(shouldShowStrikethroughPrice) this.showStrikeThroughPrice()

        this.disclaimerText = createParagraphIn(this.productDiv, {key: 'strings:colonistCommon.utils.emptyString'}, 'store_page_membership_disclaimer', '')
        const disclaimerKey = this.getDisclaimerKey()
        setElementInnerHTML(this.disclaimerText, disclaimerKey)

        this.buttonsContainer = createDivIn(this.productDiv, 'store-button-membership-container')

        this.subscribeButton = createDivIn(this.buttonsContainer, 'store-button-membership')

        this.subscribeButtonText = createParagraphIn(this.subscribeButton, {key: 'strings:storePage.sections.membership.action.subscribe'}, '', '')

        if(!giftOverlay) return
        this.giftButton = createDivIn(this.buttonsContainer, 'store-button-gift')
        createImgIn(this.giftButton, LobbyImage.StoreGiftMembership)
        const giftButtonClickEventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ABTestClickAnalytics,
            category: AnalyticEventCategory.GiftMembershipOverlay,
            floatValue: GiftMembershipMethod.Store,
        }
        setElementOnClick(this.giftButton, () => {
            void CA.sendAnalyticEvent(giftButtonClickEventData)
            doActionIfCanGiftMembership(() => {
                giftOverlay.show()
                giftOverlay.reorderPages(false)
                giftOverlay.selectGiftTier(this.membershipProduct.type)
            })
        }, false, true)
    }

    protected getDisclaimerKey(): TranslationObject {
        const annualPrice = this.getMembershipPriceString('annual')

        const context = this.localizedPrice ? 'localized' : ''
        if(this.membershipProduct.billedAnnually) return {key: 'strings:storePage.sections.membership.billing.annually', options: {context: context, amount: this.localizedPrice ?? annualPrice}}

        return {key: 'strings:storePage.sections.membership.billing.monthly'}
    }

    private createDefaultAction() {
        setElementOnClick(this.productDiv, () => {
            AudioManager.play(AudioType.Click)
            CA.sendGoogleAnalyticsEvent(CA.Category.Store, CA.Label.StoreClickedSubscribe, this.getColonistMembershipType())
            this.sendClickAnalytic()
            doActionIfLoggedIn(() => {
                this.sendToCheckout()
            })
        })
    }

    protected getMembershipPriceString(requestedPricing: 'annual' | 'monthly' = 'monthly'): string {
        if(this.localizedPrice) return this.convertLocalizedPriceToMonthly()
        const monthlyPrice = this.membershipProduct.price / 100
        const isAnnualRequested = requestedPricing == 'annual'
        const price = isAnnualRequested ? monthlyPrice * 12 : monthlyPrice

        return `${price}`
    }

    private convertLocalizedPriceToMonthly(): string {
        if(!this.localizedPrice) return ''
        if(!this.membershipProduct.billedAnnually) return this.localizedPrice

        const priceWithoutCurrency = this.localizedPrice.replace(/[^0-9,.]/g, '').replace(',', '.')
        const monthlyPrice = Math.floor(parseFloat(priceWithoutCurrency) * 100 / 12) / 100
        return `${monthlyPrice}`
    }

    protected showStrikeThroughPrice() {
        const monthlyPrice = ColonistMembershipProduct.getMonthlyPriceOfAnnualMembership(this.getColonistMembershipType())
        if(monthlyPrice == undefined) return

        const strikethourghPrice = createParagraphIn(this.priceDiv, {key: 'strings:storePage.sections.membership.billing.paidMonthly', options: {context: 'strikethrough', amount: `${monthlyPrice}`}}, 'strikethrough-price', '')
        this.priceDiv.insertBefore(strikethourghPrice, this.priceDiv.firstChild)
    }

    protected sendToCheckout() {
        if(this.membershipProduct.oneTimePurchaseType != null) {
            navigateToPage(UIAPIEndpoints.checkoutOneTimePurchaseUrl(this.membershipProduct.oneTimePurchaseType))
            return
        }
        navigateToPage(UIAPIEndpoints.checkoutMembershipUrl(this.getColonistMembershipType()))
    }

    protected sendClickAnalytic() {
        const membershipType = this.getColonistMembershipType()
        let clickAnalytic: AnalyticEventValue = AnalyticEventValue.StoreMembershipPlusMonthly

        switch(membershipType) {
            case ColonistMembershipType.PlusMonthly:
                clickAnalytic = AnalyticEventValue.StoreMembershipPlusMonthly
                break
            case ColonistMembershipType.PremiumMonthly:
                clickAnalytic = AnalyticEventValue.StoreMembershipPremiumMonthly
                break
            case ColonistMembershipType.EliteMonthly:
                clickAnalytic = AnalyticEventValue.StoreMembershipEliteMonthly
                break
            case ColonistMembershipType.PlusYearly:
                clickAnalytic = AnalyticEventValue.StoreMembershipPlusYearly
                break
            case ColonistMembershipType.PremiumYearly:
                clickAnalytic = AnalyticEventValue.StoreMembershipPremiumYearly
                break
            case ColonistMembershipType.EliteYearly:
                clickAnalytic = AnalyticEventValue.StoreMembershipEliteYearly
                break
            case ColonistMembershipType.Holiday2023AllAccessPass:
                clickAnalytic = AnalyticEventValue.StoreMembershipHoliday2023AllAccessPass
                break
        }

        const eventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ABTestClickAnalytics,
            category: AnalyticEventCategory.ABTestClickAnalytics,
            floatValue: clickAnalytic,
        }
        void CA.sendAnalyticEvent(eventData)
    }

    protected getColonistMembershipType(): ColonistMembershipType {
        if(this.storeState.membershipToggleYearlyActive) {
            if(this.membershipProduct.type == ColonistMembershipType.PlusMonthly) return ColonistMembershipType.PlusYearly
            if(this.membershipProduct.type == ColonistMembershipType.PremiumMonthly) return ColonistMembershipType.PremiumYearly
            if(this.membershipProduct.type == ColonistMembershipType.EliteMonthly) return ColonistMembershipType.EliteYearly
        }
        return this.membershipProduct.type
    }

    changeToDowngradeButton() {
        this.removeButtonStyling()
        setElementInnerHTML(this.subscribeButtonText, {key: 'strings:storePage.sections.membership.action.downgrade'})
        setElementOnClick(this.productDiv, () => {
            AudioManager.play(AudioType.Click)
            CA.sendGoogleAnalyticsEvent(CA.Category.Store, CA.Label.StoreClickedSubscribe, this.getColonistMembershipType())
            this.sendClickAnalytic()
            doActionIfLoggedIn(() => {
                this.manageSubscription()
            })
        })
    }

    changeToManageButton() {
        this.removeButtonStyling()
        setElementInnerHTML(this.subscribeButtonText, {key: 'strings:storePage.sections.membership.action.manage'})
        this.subscribeButton.classList.add('button_owned')
        setElementOnClick(this.productDiv, () => {
            AudioManager.play(AudioType.Click)
            CA.sendGoogleAnalyticsEvent(CA.Category.Store, CA.Label.StoreClickedSubscribe, this.getColonistMembershipType())
            this.sendClickAnalytic()
            doActionIfLoggedIn(() => {
                this.manageSubscription()
            })
        })
    }

    changeToUpgradeButton() {
        this.removeButtonStyling()
        setElementInnerHTML(this.subscribeButtonText, {key: 'strings:storePage.sections.membership.action.upgrade'})
        this.subscribeButton.classList.add('button_upgrade')
        setElementOnClick(this.productDiv, () => {
            AudioManager.play(AudioType.Click)
            CA.sendGoogleAnalyticsEvent(CA.Category.Store, CA.Label.StoreClickedSubscribe, this.getColonistMembershipType())
            this.sendClickAnalytic()
            doActionIfLoggedIn(() => {
                this.manageSubscription()
                // TODO Goku: Make this one go to this screen https://i.imgur.com/CA860ea.png instead of this https://i.imgur.com/hzz9Laa.png
            })
        })
    }

    changeToGoAnnualButton() {
        this.removeButtonStyling()
        setElementInnerHTML(this.subscribeButtonText, {key: 'strings:storePage.sections.membership.action.goAnnual'})
        this.subscribeButton.classList.add('button_upgrade')
        setElementOnClick(this.productDiv, () => {
            AudioManager.play(AudioType.Click)
            CA.sendGoogleAnalyticsEvent(CA.Category.Store, CA.Label.StoreClickedSubscribe, this.getColonistMembershipType())
            this.sendClickAnalytic()
            doActionIfLoggedIn(() => {
                this.manageSubscription()
                // TODO Goku: Make this one go to this screen https://i.imgur.com/CA860ea.png instead of this https://i.imgur.com/hzz9Laa.png
            })
        })
    }

    changeToOwnedButton() {
        this.removeButtonStyling()
        setElementInnerHTML(this.subscribeButtonText, {key: 'strings:popups.owned.title'})
        this.subscribeButton.classList.add('button_owned')
        setElementOnClick(this.productDiv, () => undefined)
    }

    changeToCancelButton() {
        this.removeButtonStyling()
        setElementInnerHTML(this.subscribeButtonText, {key: 'strings:storePage.sections.membership.action.cancel'})
        this.subscribeButton.classList.add('button_owned')
        setElementOnClick(this.productDiv, () => UIStoreMembershipProductView.cancelGiftedMembership())
    }

    removeButton() {
        this.subscribeButton.classList.add('hidden')
        setElementOnClick(this.productDiv, () => undefined)
    }

    protected removeButtonStyling() {
        if(this.subscribeButton.classList.contains('button_upgrade')) this.subscribeButton.classList.remove('button_upgrade')
        if(this.subscribeButton.classList.contains('button_owned')) this.subscribeButton.classList.remove('button_owned')
    }

    // eslint-disable-next-line class-methods-use-this
    protected manageSubscription() {
        const paymentMethod = ClientUserState.userState?.items.membershipPaymentMethod
        const sendUserToDiscord = paymentMethod == PaymentMethod.Discord
        if(sendUserToDiscord) {
            UIStoreMembershipProductView.createManageSubscriptionOnDiscordPopup()
            return
        }
        void UICheckoutAjaxEndPoints.executeStripeCustomerPortalSession()
    }

    private static createManageSubscriptionOnDiscordPopup() {
        const popupTitle: TranslationObject = {key: 'strings:storePage.manageMembership.title'}
        const popupBody: TranslationObject = UIStoreMembershipProductView.getManageSubscriptionOnDiscordPopupBody()
        UIPopupController.createSimplePopup(popupTitle, popupBody)
    }

    private static getManageSubscriptionOnDiscordPopupBody(): TranslationObject {
        const discordMembership = ClientUserState.userState?.items.getCurrentMembership()
        if(discordMembership == undefined) throw new Error('Membership not found')

        const hasDiscordAnnualMembership = ColonistMembershipProduct.isAnnualMembership(discordMembership.type)
        if(hasDiscordAnnualMembership) return {key: 'strings:storePage.manageMembership.body', options: {context: 'waitForExpiration'}}

        return {key: 'strings:storePage.manageMembership.body', options: {context: 'updateOnDiscord'}}
    }

    private static async cancelGiftedMembership() {
        const popup = new ViewPopup({key: 'strings:storePage.sections.membership.cancelConfirmation.title'},
            {key: 'strings:storePage.sections.membership.cancelConfirmation.body'},
            async() => await UIStoreAjaxEndPoints.cancelGiftedMembership(),
            () => undefined,
            true)
        popup.show()
    }
}
